import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Box,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AttributeGroup } from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { MasterFormatDivision } from '../../../api/protosCompiled/masterFormatDivision/masterFormatDivision_pb';
import { useStore } from 'zustand';
import { useGlobalItemStore } from '../../../stores/globalItemStore';

interface AttributeGroupSelectorProps {
  filterDivision: string;
  setFilterDivision: (value: string) => void;
  selectedAttributeGroupId: string | null;
  setSelectedAttributeGroupId: (value: string) => void;
  attributeGroups?: AttributeGroup.AsObject[];
  masterFormatDivision?: MasterFormatDivision.AsObject[];
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const AttributeGroupSelector: React.FC<AttributeGroupSelectorProps> = ({
  filterDivision,
  setFilterDivision,
  selectedAttributeGroupId,
  setSelectedAttributeGroupId,
  attributeGroups,
  masterFormatDivision,
  required = false,
  error = false,
  helperText = '',
}) => {
  const theme = useTheme();
  const { currentItem } = useStore(useGlobalItemStore);

  const selectStyles = {
    '& .MuiSelect-select': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* First section - Filter by Division */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <Tooltip
            title="Filter attribute groups by their associated division"
            enterDelay={500}
            leaveDelay={200}
          >
            <IconButton
              size="small"
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                mr: 1,
                mb: 1,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <FormControl fullWidth>
            <InputLabel id="filter-division-label">Filter by Division</InputLabel>
            <Select
              labelId="filter-division-label"
              value={filterDivision}
              onChange={e => setFilterDivision(e.target.value)}
              label="Filter by Division"
              disabled={
                currentItem
                  ? currentItem?.costsList &&
                    currentItem.costsList.length > 0 &&
                    currentItem.costsList[0].uomid !== 0
                  : false
              }
              sx={selectStyles}
            >
              <MenuItem value="">All Divisions</MenuItem>
              {attributeGroups &&
                masterFormatDivision
                  ?.filter(division =>
                    attributeGroups.some(group => group.masterformatid === division.id)
                  )
                  .map(division => (
                    <MenuItem key={division.id} value={division.id}>
                      {`${division.divisionnumber} - ${division.divisionname}`}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <Tooltip
            title="Attribute groups define the key characteristics of the item (e.g., size, color, material). Select a group to assign specific attributes to this item."
            enterDelay={500}
            leaveDelay={200}
          >
            <IconButton
              size="small"
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                mr: 1,
                mb: 1,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <FormControl fullWidth required={required} error={error}>
            <InputLabel id="attribute-group-label">Attribute Group</InputLabel>
            <Select
              labelId="attribute-group-label"
              value={selectedAttributeGroupId || ''}
              onChange={e => setSelectedAttributeGroupId(e.target.value)}
              label="Attribute Group"
              disabled={
                currentItem
                  ? currentItem?.costsList &&
                    currentItem.costsList.length > 0 &&
                    currentItem.costsList[0].uomid !== 0
                  : false
              }
              sx={selectStyles}
            >
              <MenuItem value="">Select Attribute Group</MenuItem>
              {attributeGroups
                ?.filter(group => !filterDivision || group.masterformatid === filterDivision)
                .sort((a, b) => a.attributegroupname.localeCompare(b.attributegroupname))
                .map(group => (
                  <MenuItem key={group.attributegroupid} value={group.attributegroupid.toString()}>
                    {group.attributegroupname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </>
  );
};

export default React.memo(AttributeGroupSelector);
